import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import PropTypes from 'prop-types';
import { withLwtStore } from '@lwtears/lwt-common-frontend/lib/LwtApp';
import { getLocale, setLocale } from '@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import AudioCaptions from 'components/common/AudioCaptions';
import Background from 'components/common/Theme/Background';
import { LOGIN_STEP as STEP } from 'state/login/types';
import {
  goToStep,
  selectClass,
  postSinglePin,
  loginUser,
  selectUser,
  requestClassrooms,
  clearLoginError
} from 'state/login/actions';
import { loadCommonAssets, unloadLoginSounds } from 'state/ui/actions';
import storage from 'store';
import HowlerWithCCs from 'utils/captionsHelper';
import LoginEducatorPin from './EducatorPin';
import LoginClasses from './ClassList';
import LoginUsers from './UserList';
import styles from '../style/Login.module.scss';
import StudentPin from './StudentPin';

const { SHOW_LOGIN, SHOW_CLASSES, SHOW_USERS, SHOW_PIN } = STEP;

class Login extends React.Component {
  async componentDidMount() {
    const { loadCommonAssets } = this.props;

    const pins = storage.get('pins') || [];
    if (pins.length) {
      await this.props.requestClassrooms(pins);
    }

    await loadCommonAssets();
  }

  async componentDidUpdate(prevProps) {
    const {
      props: { classrooms, selectedClassId, loading, step, loginSounds, success }
    } = this;

    const { sounds, captions } = loginSounds ? loginSounds : { sounds: null, captions: null };

    let usedLocale = getLocale();

    if (step < prevProps.step && step <= STEP.SHOW_CLASSES) {
      await setLocale('en-US');
      usedLocale = 'en-US';
    }

    if (
      loading ||
      (step === prevProps.step && prevProps.loading === loading) ||
      !sounds ||
      !sounds[usedLocale]
    )
      return;

    if (step === STEP.SHOW_USERS) {
      const classroom = classrooms.find((c) => c.id === selectedClassId);
      if (classroom && classroom.locale) {
        usedLocale = classroom.locale;
      } else {
        // play en-US if no locale
        usedLocale = 'en-US';
      }

      await setLocale(usedLocale);
    }

    if (step === STEP.SHOW_PIN && !loading && success) return;

    Object.keys(sounds).forEach((key) =>
      Object.keys(sounds[key]).forEach((subKey) => {
        return sounds[key][subKey].stop();
      })
    );

    const subscribeAndPlay = (sound, captions) => {
      const {
        settings: {
          accessibility: { audioSettings }
        }
      } = this.props.lwtStoreState;

      HowlerWithCCs(sound, captions);
      audioSettings.muteSounds ? sound.volume(0) : sound.volume(audioSettings.volume / 100);
      sound.play();
    };

    switch (step) {
      case SHOW_LOGIN:
        subscribeAndPlay(
          sounds[usedLocale]['enterYourTeachersPin'],
          captions[usedLocale]['enterYourTeachersPin']
        );
        break;
      case SHOW_CLASSES:
        if (classrooms.length > 0) {
          subscribeAndPlay(
            sounds[usedLocale]['chooseYourClass'],
            captions[usedLocale]['chooseYourClass']
          );
        }
        break;
      case SHOW_USERS:
        subscribeAndPlay(
          sounds[usedLocale]['chooseYourName'],
          captions[usedLocale]['chooseYourName']
        );
        break;
      case SHOW_PIN:
        subscribeAndPlay(
          sounds[usedLocale]['enterYourSecretCode'],
          captions[usedLocale]['enterYourSecretCode']
        );
        break;
      default:
        return;
    }
  }

  componentWillUnmount() {
    // unload sounds used for login
    this.props.unloadLoginSounds();
  }

  render() {
    const {
      error,
      selectUser,
      goToStep,
      users,
      classrooms,
      hasNewClassrooms,
      selectClass,
      step,
      loggedIn,
      loginUser,
      loading,
      postSinglePin,
      selectedClassId,
      selectedUserId,
      success,
      clearLoginError
    } = this.props;

    const {
      settings: {
        accessibility: {
          displaySettings: { contentScaling }
        }
      }
    } = this.props.lwtStoreState;

    const studentPinProps = {
      contentScaling,
      error,
      goToStep,
      loading,
      loginUser,
      selectedClassId,
      selectedUserId,
      success,
      users,
      clearLoginError
    };

    return (
      <Background className={styles.loginComponent}>
        <SwitchTransition mode="out-in">
          <CSSTransition
            key={step}
            timeout={200}
            classNames={{
              enter: 'fade-enter',
              enterActive: 'fade-enter-active',
              exit: 'fade-exit'
            }}
          >
            <Fragment>
              {step === SHOW_LOGIN && loading && (
                <div className={[styles.loadingOverlay, 'loader'].join(' ')}></div>
              )}
              {step === SHOW_LOGIN && !loading && (
                <LoginEducatorPin
                  {...{ error, loggedIn, loading, postSinglePin, contentScaling }}
                />
              )}
              {step === SHOW_USERS && (
                <LoginUsers {...{ selectUser, goToStep, users, contentScaling }} />
              )}
              {step === SHOW_CLASSES && (
                <LoginClasses
                  {...{ classrooms, goToStep, selectClass, hasNewClassrooms, contentScaling }}
                />
              )}
              {step === SHOW_PIN && (
                <StudentPin contentScaling={contentScaling} {...studentPinProps} />
              )}
            </Fragment>
          </CSSTransition>
        </SwitchTransition>
        <AudioCaptions />
      </Background>
    );
  }
}

Login.propTypes = {
  loggedIn: PropTypes.bool,
  loading: PropTypes.bool,
  step: PropTypes.number,
  selectedUserId: PropTypes.number,
  error: PropTypes.object,
  users: PropTypes.array,
  classrooms: PropTypes.array,
  hasNewClassrooms: PropTypes.bool,
  loginUser: PropTypes.func.isRequired,
  selectUser: PropTypes.func.isRequired,
  selectClass: PropTypes.func.isRequired,
  goToStep: PropTypes.func.isRequired,
  requestClassrooms: PropTypes.func.isRequired,
  postSinglePin: PropTypes.func.isRequired,
  loadCommonAssets: PropTypes.func.isRequired,
  selectedClassId: PropTypes.number,
  commonSounds: PropTypes.object,
  loginSounds: PropTypes.object,
  unloadLoginSounds: PropTypes.func,
  clearLoginError: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
  step: state.login.step,
  classrooms: state.login.classrooms,
  hasNewClassrooms: state.login.hasNewClassrooms,
  loading: state.ui.loading,
  error: state.login.error,
  success: state.login.success,
  loggedIn: state.login.loggedIn,
  users: state.login.users,
  selectedUserId: state.login.selectedUserId,
  commonSounds: state.ui.sounds.common,
  loginSounds: state.ui.sounds.login,
  selectedClassId: state.login.classroom.id
});

const mapDispatchToProps = {
  goToStep,
  selectClass,
  postSinglePin,
  requestClassrooms,
  loginUser,
  selectUser,
  loadCommonAssets,
  unloadLoginSounds,
  clearLoginError
};

export default connect(mapStateToProps, mapDispatchToProps)(withLwtStore(Login));
